import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, LifeBuoy, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { DateField } from "../admin/DateField";
import { Switch } from "../ui/switch";
import { docFollowup, docFollowupReminder, docSetKit } from "../../lib/api";

// After filing an insurance card or ID: add it to the emergency kit and confirm (or set) its renewal reminder.
export const UploadFollowUp = ({ docId, onClose }) => {
  const [info, setInfo] = useState(null);
  const [kit, setKit] = useState(true);
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { docFollowup(docId).then((r) => (r.eligible ? setInfo(r) : onClose())).catch(onClose); }, [docId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!info) return null;
  const save = async () => {
    setBusy(true);
    try {
      if (kit !== info.in_kit) await docSetKit(docId, kit);
      if (!info.reminders.length && date) await docFollowupReminder(docId, date);
      toast.success([kit && "Added to your emergency kit", !info.reminders.length && date && `reminder set for ${date}`].filter(Boolean).join(" · ") || "Saved");
      onClose();
    } catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Couldn't save"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="upload-followup">
        <DialogHeader><DialogTitle className="font-serif text-xl">Two quick things for {info.title}</DialogTitle>
          <DialogDescription>Cards and IDs are what your family reaches for first in an emergency.</DialogDescription></DialogHeader>
        <label className="flex items-start gap-3 rounded-xl border border-border p-3 cursor-pointer" data-testid="upload-followup-kit">
          <LifeBuoy className="h-5 w-5 text-primary shrink-0" />
          <span className="flex-1 text-sm"><b>Add to the emergency kit</b><span className="block text-xs text-muted-foreground">It shows on the Emergency page and in what your executor sees first.</span></span>
          <Switch checked={kit} onCheckedChange={setKit} data-testid="upload-followup-kit-switch" />
        </label>
        <div className="rounded-xl border border-border p-3 flex items-start gap-3" data-testid="upload-followup-reminder">
          <BellRing className="h-5 w-5 text-primary shrink-0" />
          {info.reminders.length ? (
            <div className="text-sm" data-testid="upload-followup-reminder-found"><b>Renewal reminder is set</b>{info.reminders.map((r, i) => <span key={i} className="block text-xs text-muted-foreground">{r.label} · {r.date}</span>)}</div>
          ) : (
            <div className="flex-1 text-sm"><b>No expiry date found</b><span className="block text-xs text-muted-foreground mb-2">Add it and we'll remind you before it runs out.</span>
              <DateField value={date} onChange={setDate} placeholder="Expiry date" fromDate={new Date()} className="w-full" testid="upload-followup-date" /></div>
          )}
        </div>
        <div className="flex gap-2"><Button className="rounded-full flex-1" onClick={save} disabled={busy} data-testid="upload-followup-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          <Button variant="ghost" className="rounded-full" onClick={onClose} data-testid="upload-followup-skip">Skip</Button></div>
      </DialogContent>
    </Dialog>
  );
};
